import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import * as programApi from "../../api/outreach-program.api"
import { formatCentral, apiErrorMessage } from "../../lib/program"
import type { DisableResult, ProgramAction, ProgramStatus } from "../../types/outreach-program.types"

type ControlAction = "enable_live" | Exclude<ProgramAction, "enable">

const CONFIRM: Record<
  ControlAction,
  { title: string; body: string; button: string; needsReason: boolean; destructive?: boolean }
> = {
  enable_live: {
    title: "Enable the program live?",
    body:
      "The 10-minute tick will book real consent and check-in calls and texts for this clinic, inside the call window and caps below.",
    button: "Enable live",
    needsReason: false,
  },
  shadow: {
    title: "Switch to shadow mode?",
    body:
      "The tick records what it would do (the plan list below) and books nothing. Calls and texts already booked still go out; pause to stop those.",
    button: "Switch to shadow",
    needsReason: false,
  },
  pause: {
    title: "Pause this clinic's outreach?",
    body:
      "Every pending call and text is skipped when it comes due and the tick books nothing new. Bookings are kept, so resuming (Enable) is instant.",
    button: "Pause",
    needsReason: true,
  },
  disable: {
    title: "Disable the program?",
    body:
      "Disabling cancels every pending call and text for this clinic, including calls patients asked for. One review item will list the patient-requested calls so staff can rebook them. Enrollments stay open, so attempt counts survive a re-enable. Start N buttons still work as one-off manual runs.",
    button: "Disable and cancel everything",
    needsReason: true,
    destructive: true,
  },
}

export function ProgramStatusBadge({ program }: { program: ProgramStatus }) {
  const tone =
    program.status === "enabled"
      ? program.mode === "live"
        ? "bg-success/10 text-success ring-success/30"
        : "bg-info/10 text-info ring-info/30"
      : program.status === "paused"
        ? "bg-warning/10 text-warning ring-warning/30"
        : "bg-muted/50 text-muted-foreground ring-border"
  return (
    <span
      data-testid="program-status"
      className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset", tone)}
    >
      {program.status}
      {program.status === "enabled" && <span className="font-normal">· {program.mode}</span>}
    </span>
  )
}

interface Props {
  orgId: string
  program: ProgramStatus
  onChanged: (program: ProgramStatus) => void
}

export function ProgramControls({ orgId, program, onChanged }: Props) {
  const [pending, setPending] = useState<ControlAction | null>(null)
  const [reason, setReason] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [disabled, setDisabled] = useState<DisableResult | null>(null)

  const open = (a: ControlAction) => {
    setPending(a)
    setReason("")
    setError(null)
  }

  const confirm = async () => {
    if (!pending) return
    setBusy(true)
    setError(null)
    try {
      if (pending === "disable") {
        const res = await programApi.disableProgram(orgId, reason.trim())
        setDisabled(res)
        onChanged(res.program)
      } else if (pending === "pause") {
        onChanged(await programApi.pauseProgram(orgId, reason.trim()))
      } else if (pending === "shadow") {
        onChanged(await programApi.shadowProgram(orgId))
      } else {
        onChanged(await programApi.enableProgram(orgId, "live"))
      }
      setPending(null)
    } catch (err: any) {
      setError(apiErrorMessage(err, "The change failed"))
    } finally {
      setBusy(false)
    }
  }

  const cfg = pending ? CONFIRM[pending] : null
  const isLive = program.status === "enabled" && program.mode === "live"
  const isShadow = program.status === "enabled" && program.mode === "shadow"

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={isLive} onClick={() => open("enable_live")}>
          Enable live
        </Button>
        <Button size="sm" variant="outline" disabled={isShadow} onClick={() => open("shadow")}>
          Shadow
        </Button>
        <Button size="sm" variant="outline" disabled={program.status === "paused"} onClick={() => open("pause")}>
          Pause
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-destructive"
          disabled={program.status === "disabled"}
          onClick={() => open("disable")}
        >
          Disable
        </Button>
      </div>

      {disabled && (
        <div
          className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm space-y-1"
          data-testid="disable-result"
        >
          <p className="font-medium">
            Program disabled. Cancelled {disabled.cancelled.calls} call{disabled.cancelled.calls === 1 ? "" : "s"} and{" "}
            {disabled.cancelled.texts} text{disabled.cancelled.texts === 1 ? "" : "s"} for {disabled.cancelled.patients}{" "}
            patient{disabled.cancelled.patients === 1 ? "" : "s"}.
          </p>
          {disabled.cancelled.patient_requested.length > 0 ? (
            <>
              <p>
                {disabled.cancelled.patient_requested.length} of those were calls patients asked for
                {disabled.cancelled.review_item_id
                  ? `; review item ${disabled.cancelled.review_item_id} lists them for rebooking.`
                  : "."}
              </p>
              <ul className="text-xs text-muted-foreground">
                {disabled.cancelled.patient_requested.slice(0, 10).map((r) => (
                  <li key={r.scheduled_call_id}>
                    {r.patient_id} · {formatCentral(r.at)}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-muted-foreground">No patient-requested calls were cancelled.</p>
          )}
        </div>
      )}

      <Dialog open={!!pending} onOpenChange={(o) => !o && !busy && setPending(null)}>
        <DialogContent className="max-w-lg">
          {cfg && (
            <>
              <DialogHeader>
                <DialogTitle>{cfg.title}</DialogTitle>
                <DialogDescription>{cfg.body}</DialogDescription>
              </DialogHeader>
              {cfg.needsReason && (
                <div className="space-y-1.5">
                  <Label htmlFor="program-reason">Reason</Label>
                  <Input
                    id="program-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why (shown on the page and in the audit log)"
                  />
                </div>
              )}
              {error && <p className="text-sm text-destructive">{error}</p>}
              <DialogFooter>
                <Button variant="outline" onClick={() => setPending(null)} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  variant={cfg.destructive ? "destructive" : "default"}
                  onClick={confirm}
                  disabled={busy || (cfg.needsReason && !reason.trim())}
                >
                  {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {cfg.button}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
